import { useMemo } from 'react';
import { useAppSelector } from './redux';
import { getAABB } from '../utils/topology';

export interface FloorBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function useFloorBounds(): FloorBounds {
  const floorplanBounds = useAppSelector((state) => state.floorplan.bounds);
  const elements = useAppSelector((state) => state.layout.elements);

  return useMemo(() => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    // 1. Incorporate DXF floorplan bounds if visible and present
    if (floorplanBounds) {
      minX = floorplanBounds.minX;
      minY = floorplanBounds.minY;
      maxX = floorplanBounds.maxX;
      maxY = floorplanBounds.maxY;
    }

    // 2. Incorporate user-placed layout elements
    Object.values(elements).forEach((el) => {
      if (!el.metadata.visible) return;
      const box = getAABB(el);
      if (box.minX < minX) minX = box.minX;
      if (box.minY < minY) minY = box.minY;
      if (box.maxX > maxX) maxX = box.maxX;
      if (box.maxY > maxY) maxY = box.maxY;
    });

    // 3. Fallbacks if bounds are still infinite (e.g. empty stage)
    if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
      return { minX: 0, minY: 0, maxX: 4000, maxY: 3000 };
    }

    // Ensure bounds have some width/height to avoid divide by zero
    const width = maxX - minX;
    const height = maxY - minY;
    if (width <= 0) {
      maxX = minX + 100;
    }
    if (height <= 0) {
      maxY = minY + 100;
    }

    return { minX, minY, maxX, maxY };
  }, [floorplanBounds, elements]);
}
